import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from './notify';
import { Pill, X, Undo2, Ban } from 'lucide-react';
import { VisitCard, money } from './VisitBits';
import ConfirmDialog from '../../components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PharmacyPanel({ visits, refresh, authCfg, role, isSuperAdmin }) {
  const canAct = isSuperAdmin || role === 'Pharmacist' || role === 'Admin';
  const queue = visits.filter((v) => v.status === 'PHARMACY_PENDING' || (v.status === 'ADMITTED' && (v.prescriptions || []).some((p) => p.status === 'pending')));

  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]); // {rx_id, item_name, instructions, cost, quantity, give}
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const openVisit = (visit) => {
    setActive(visit);
    setRows(
      (visit.prescriptions || [])
        .filter((p) => p.status === 'pending')
        .map((p) => ({ rx_id: p.rx_id, item_name: p.item_name, instructions: p.instructions, cost: p.cost, quantity: p.quantity, give: true }))
    );
  };

  const setQty = (rx_id, q) => setRows((prev) => prev.map((r) => (r.rx_id === rx_id ? { ...r, quantity: Math.max(1, parseInt(q) || 1) } : r)));
  const toggleGive = (rx_id) => setRows((prev) => prev.map((r) => (r.rx_id === rx_id ? { ...r, give: !r.give } : r)));

  const given = rows.filter((r) => r.give);
  const notGiven = rows.filter((r) => !r.give);
  const total = given.reduce((s, r) => s + r.cost * r.quantity, 0);

  const dispense = () => {
    if (notGiven.length > 0) {
      setConfirmOpen(true);
      return;
    }
    doDispense();
  };

  const doDispense = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/visits/${active.visit_id}/pharmacy/dispense`,
        { items: given.map((r) => ({ rx_id: r.rx_id, quantity: r.quantity })) },
        authCfg
      );
      toast.success(`${res.data.dispensed} medication(s) dispensed${res.data.not_given ? `, ${res.data.not_given} not given` : ''} ${res.data.status === 'ADMITTED' ? ' — patient remains admitted' : ' — visit sent to billing'}`);
      setActive(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dispense medications');
    } finally {
      setSubmitting(false);
    }
  };

  if (active) {
    return (
      <Card className="border-cyan-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-cyan-50 to-cyan-100 border-b border-cyan-200 p-4">
          <CardTitle className="flex items-center justify-between text-cyan-900 text-base">
            <span className="flex items-center gap-2"><Pill className="w-5 h-5" /> Dispense — {active.patient_name}</span>
            <Button variant="outline" size="sm" onClick={() => setActive(null)} data-testid="pharmacy-close">
              <X className="w-4 h-4 mr-1" /> Back to queue
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            {active.visit_id} • Serial: {active.patient_serial_number}
            {(active.consultation?.final_diagnosis || active.consultation?.provisional_diagnosis || active.consultation?.diagnosis) && <span> • Diagnosis: <span className="font-medium text-gray-800">{active.consultation.final_diagnosis || active.consultation.provisional_diagnosis || active.consultation.diagnosis}</span></span>}
          </div>

          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.rx_id}
                className={`border rounded-lg p-3 flex items-center gap-3 flex-wrap ${r.give ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50 opacity-80'}`}
                data-testid={`dispense-row-${r.rx_id}`}
              >
                <div className="flex-1 min-w-[180px]">
                  <p className={`text-sm font-semibold ${r.give ? 'text-gray-800' : 'text-red-700 line-through'}`}>{r.item_name}</p>
                  <p className="text-xs text-gray-500">{r.instructions || 'As directed by the doctor'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Qty</span>
                  <Input
                    type="number"
                    min="1"
                    value={r.quantity}
                    disabled={!r.give}
                    onChange={(e) => setQty(r.rx_id, e.target.value)}
                    className="h-8 w-16 text-center"
                    data-testid={`dispense-qty-${r.rx_id}`}
                  />
                </div>
                <span className={`text-sm w-24 text-right ${r.give ? 'text-gray-700' : 'text-red-400 line-through'}`}>{money(active, r.cost * r.quantity)}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleGive(r.rx_id)}
                  className={r.give ? 'border-red-300 text-red-600 hover:bg-red-50 h-8' : 'border-green-300 text-green-700 hover:bg-green-50 h-8'}
                  data-testid={`dispense-toggle-${r.rx_id}`}
                >
                  {r.give ? (<><Ban className="w-3.5 h-3.5 mr-1" /> Not given</>) : (<><Undo2 className="w-3.5 h-3.5 mr-1" /> Restore</>)}
                </Button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between p-3 bg-cyan-50 border border-cyan-200 rounded-lg text-sm">
            <span className="text-cyan-900 font-medium">
              To dispense: {given.length} item(s){notGiven.length > 0 ? ` • Not given: ${notGiven.length}` : ''}
            </span>
            <span className="text-lg font-bold text-cyan-900" data-testid="dispense-total">{money(active, total)}</span>
          </div>

          <Button onClick={dispense} disabled={submitting} className="w-full bg-cyan-600 hover:bg-cyan-700 h-11" data-testid="pharmacy-dispense-submit">
            {submitting ? 'Saving…' : given.length === 0 ? 'Confirm: No Medications Given — Send to Billing' : `Dispense ${given.length} & Send to Billing`}
          </Button>

          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Some medications will not be given"
            description={`${notGiven.length} medication(s) marked as NOT given:\n${notGiven.map((r) => `• ${r.item_name}`).join('\n')}\n\nThey will be excluded from the bill.`}
            confirmLabel="Dispense & Continue"
            destructive
            onConfirm={doDispense}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Pill className="w-5 h-5 text-cyan-600" /> Pharmacy Queue ({queue.length})
      </h2>
      {queue.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="pharmacy-empty">
          No prescriptions waiting to be dispensed.
        </div>
      )}
      {queue.map((v) => (
        <VisitCard
          key={v.visit_id}
          visit={v}
          showHospital={isSuperAdmin || role === 'ViewOnly'}
          actionLabel={canAct ? `Dispense (${(v.prescriptions || []).filter((p) => p.status === 'pending').length})` : null}
          onAction={canAct ? () => openVisit(v) : null}
          actionTestId={`dispense-${v.visit_id}`}
        />
      ))}
    </div>
  );
}
